import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import axios from "axios";
import { toast } from "react-hot-toast";

const GoogleAccountConnect: React.FC = () => {
  const { user } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    // Check URL parameters for connection status
    const urlParams = new URLSearchParams(window.location.search);
    const googleStatus = urlParams.get("google");

    if (googleStatus) {
      if (googleStatus === "connected") {
        toast.success("Google account connected successfully!");
      } else if (googleStatus === "error") {
        toast.error("Failed to connect Google account");
      } else if (googleStatus === "token_error") {
        toast.error("Google account connected but token verification failed");
      }

      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const handleConnectGoogle = async () => {
    try {
      setIsConnecting(true);

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/auth/google/url`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Open Google OAuth URL in a popup or new window
      const authWindow = window.open(
        response.data.authUrl,
        "google-auth",
        "width=600,height=700,scrollbars=yes,resizable=yes"
      );

      // Listen for the callback
      const checkClosed = setInterval(() => {
        if (authWindow?.closed) {
          clearInterval(checkClosed);
          setIsConnecting(false);
          // Refresh the page to get updated user data
          window.location.reload();
        }
      }, 1000);
    } catch (error: any) {
      console.error("Error getting Google auth URL:", error);
      toast.error("Failed to connect Google account");
      setIsConnecting(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/api/auth/google/disconnect`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("Google account disconnected successfully");
      // Refresh user data to reflect the disconnection
      refreshUser();
    } catch (error: any) {
      console.error("Error disconnecting Google account:", error);
      toast.error("Failed to disconnect Google account");
    }
  };

  // Check if user has Google account connected
  const hasGoogleAccount = user?.googleTokens?.access_token;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-4">Google Account Connection</h2>
      <p className="text-gray-600 mb-4">
        Connect your Google account to schedule live sessions with Google Meet.
      </p>

      {hasGoogleAccount ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
            <svg
              className="w-5 h-5 text-green-600"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <div>
              <p className="font-medium text-green-800">
                Google Account Connected
              </p>
              <p className="text-sm text-green-600">
                {user.googleProfile?.email || "Connected"}
              </p>
            </div>
          </div>

          <button
            onClick={handleDisconnectGoogle}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Disconnect Google Account
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
            <svg
              className="w-5 h-5 text-yellow-600"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
            </svg>
            <div>
              <p className="font-medium text-yellow-800">
                Google Account Not Connected
              </p>
              <p className="text-sm text-yellow-600">
                Connect your Google account to enable live session scheduling
              </p>
            </div>
          </div>

          <button
            onClick={handleConnectGoogle}
            disabled={isConnecting}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isConnecting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Connecting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Connect Google Account
              </>
            )}
          </button>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="font-medium text-blue-800 mb-2">Why connect Google?</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Schedule live sessions with Google Meet integration</li>
          <li>• Automatic calendar events for teachers and students</li>
          <li>• Seamless meeting links shared with enrolled students</li>
          <li>• Professional meeting experience with Google Meet features</li>
        </ul>
      </div>
    </div>
  );
};

export default GoogleAccountConnect;
